<template>
  <div class="d-inline-block">
    <v-btn color="primary" plain :disabled="loading" @click="onOpen">
      <v-icon small class="mr-1">mdi-printer</v-icon>
      {{ buttonLabel }}
    </v-btn>
    <v-dialog v-model="dialog" persistent max-width="450">
      <v-card>
        <v-card-title class="headline">{{ buttonLabel }}</v-card-title>
        <v-card-text>
          <v-switch
            v-model="includeAnswers"
            label="Include correct answers"
            color="primary"
            hide-details
            class="mt-0"
          />
          <v-radio-group
            v-model="countMode"
            label="Number of questions"
            hide-details
            class="mt-4"
          >
            <v-radio label="All" value="all" />
            <v-radio label="Custom" value="custom" />
          </v-radio-group>
          <v-text-field
            v-if="countMode === 'custom'"
            v-model.number="customCount"
            type="number"
            min="1"
            label="How many questions?"
            hide-details
            class="mt-2"
            style="max-width: 200px"
          />
          <p v-if="loading" class="mt-4 mb-0 text-body-2">
            Preparing questions for print…
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn plain :disabled="loading" @click="onCancel">Cancel</v-btn>
          <v-btn tile color="primary" :loading="loading" @click="onPrint">
            Print
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { questions as questionsApi, media } from '@/services/api'
import { markdownToHtml } from '@/services/text'

const filterKeys = ['moduleId', 'componentId', 'topicId', 'procedureId']

const escapeHtml = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const markerClass = (questionType, isCorrect) => {
  const shape = questionType === 'radio' ? 'radio' : 'checkbox'
  return `print-marker print-marker-${shape}${
    isCorrect ? ' print-marker-checked' : ''
  }`
}

export default {
  props: {
    filters: { type: Object, required: true },
    contextNames: { type: Object, default: null },
    knowledgeCheck: { type: Object, default: () => ({}) },
    title: { type: String, default: 'Knowledge Check Questions' },
    buttonLabel: { type: String, default: 'Print Questions' }
  },
  data() {
    return {
      dialog: false,
      includeAnswers: false,
      countMode: 'all',
      customCount: 10,
      loading: false,
      printArea: null,
      afterPrintHandler: null
    }
  },
  computed: {
    ...mapGetters('context', {
      contextModule: 'module',
      contextComponent: 'component',
      contextTopic: 'topic',
      contextProcedure: 'procedure'
    }),
    requestedCount() {
      if (this.countMode !== 'custom') return null
      const n = parseInt(this.customCount, 10)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    resolvedNames() {
      // Explicit prop wins over Vuex context fallback
      if (this.contextNames) {
        return {
          module: this.contextNames.module,
          component: this.contextNames.component,
          topic: this.contextNames.topic,
          procedure: this.contextNames.procedure
        }
      }
      return {
        module: this.contextModule && this.contextModule.name,
        component: this.contextComponent && this.contextComponent.name,
        topic: this.contextTopic && this.contextTopic.name,
        procedure: this.contextProcedure && this.contextProcedure.name
      }
    },
    contextCrumbs() {
      const crumbs = []
      const labels = [
        ['Module', this.resolvedNames.module],
        ['Component', this.resolvedNames.component],
        ['Topic', this.resolvedNames.topic],
        ['Procedure', this.resolvedNames.procedure]
      ]
      for (const [label, name] of labels) {
        if (name) crumbs.push({ label, name })
      }
      return crumbs
    }
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    onOpen() {
      this.includeAnswers = false
      this.countMode = 'all'
      this.customCount = 10
      this.dialog = true
    },
    onCancel() {
      if (this.loading) return
      this.dialog = false
    },
    async onPrint() {
      this.loading = true
      try {
        const summaries = await this.fetchQuestionSummaries()
        const limit = this.requestedCount
        const selected = limit ? summaries.slice(0, limit) : summaries
        const questions = await this.fetchQuestionDetails(selected)
        const mediaMap = await this.fetchMediaDataUrls(questions)
        const html = this.buildHtml(questions, mediaMap, this.includeAnswers)
        await this.openPrint(html)
      } catch (err) {
        console.error('Print failed', err)
        this.cleanup()
      } finally {
        this.loading = false
        this.dialog = false
      }
    },
    async fetchQuestionSummaries() {
      const baseFilters = { deep: true }
      for (const key of filterKeys) {
        if (this.filters[key]) baseFilters[key] = this.filters[key]
      }
      const all = []
      let page = 1
      // backend page size is fixed server-side; loop until exhausted
      // safety cap so a bad paging response can't spin forever
      for (let i = 0; i < 200; i++) {
        const res = await questionsApi.search({ ...baseFilters, page })
        const body = res.data || {}
        const results = body.results || []
        all.push(...results)
        const paging = (body.meta && body.meta.paging) || {}
        if (!paging.hasNext) break
        // stop early if we already have enough for a custom count
        if (this.requestedCount && all.length >= this.requestedCount) break
        page += 1
      }
      return all
    },
    async fetchQuestionDetails(summaries) {
      // Search response does not include `choices`; fetch each question
      // by id to get full detail (text + choices + media references).
      return Promise.all(summaries.map((s) => questionsApi.fetch(s.id)))
    },
    async fetchMediaDataUrls(questions) {
      const ids = new Set()
      const addId = (item) => {
        const id = item && item.mediaId
        if (id) ids.add(id)
      }
      for (const q of questions) {
        addId(q.mediaItem)
        for (const c of q.choices || []) {
          addId(c.mediaItem)
        }
      }
      const map = {}
      await Promise.all(
        Array.from(ids).map(async (id) => {
          try {
            const dataUrl = await media.files.getDataUrl(
              { mediaId: id },
              'original',
              {}
            )
            if (dataUrl) map[id] = dataUrl
          } catch (e) {
            console.warn('Failed to load media for print', id, e)
          }
        })
      )
      return map
    },
    buildHtml(questions, mediaMap, includeAnswers) {
      const parts = []
      const push = (s) => parts.push(s)

      const pushImage = (mediaId, klass) => {
        const url = mediaId && mediaMap[mediaId]
        if (!url) return
        push(`<div class="${klass}"><img src="${url}" alt=""></div>`)
      }

      const pushQuestion = (q, qi) => {
        const sortedChoices = (q.choices || [])
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
        push('<li class="print-question">')
        push('<div class="print-question-text">')
        push(`<span class="print-question-number">${qi + 1}.</span>`)
        push(`<span>${escapeHtml(q.text)}</span>`)
        push('</div>')
        pushImage(q.mediaItem && q.mediaItem.mediaId, 'print-question-image')
        push('<ol class="print-choices">')
        for (const c of sortedChoices) {
          const isCorrect = !!c.isAnswer && includeAnswers
          const liClass = isCorrect
            ? 'print-choice print-choice-correct'
            : 'print-choice'
          push(`<li class="${liClass}">`)
          push(
            `<span class="${markerClass(q.questionType, isCorrect)}"></span>`
          )
          push(`<span class="print-choice-label">${escapeHtml(c.label)}</span>`)
          pushImage(c.mediaItem && c.mediaItem.mediaId, 'print-choice-image')
          push('</li>')
        }
        push('</ol>')
        push('</li>')
      }

      push('<div class="print-header">')
      const crumbs = this.contextCrumbs
      if (crumbs.length) {
        push('<dl class="print-context">')
        for (const c of crumbs) {
          push(`<dt>${escapeHtml(c.label)}</dt>`)
          push(`<dd>${escapeHtml(c.name)}</dd>`)
        }
        push('</dl>')
      }
      push(`<h1>${escapeHtml(this.title)}</h1>`)
      if (this.knowledgeCheck.instructions) {
        push('<div class="print-instructions">')
        push(markdownToHtml(this.knowledgeCheck.instructions))
        push('</div>')
      }
      push('</div>')

      push('<ol class="print-questions">')
      if (questions.length) {
        questions.forEach(pushQuestion)
      } else {
        push('<li class="print-empty">No questions to print.</li>')
      }
      push('</ol>')

      return parts.join('')
    },
    async openPrint(html) {
      this.cleanup()
      const printArea = document.createElement('div')
      printArea.className = 'print-area'
      printArea.innerHTML = html
      document.body.appendChild(printArea)
      this.printArea = printArea

      this.afterPrintHandler = () => this.cleanup()
      window.addEventListener('afterprint', this.afterPrintHandler)

      document.body.classList.add('printing')

      await this.$nextTick()
      await this.waitForImages(printArea)

      window.print()
    },
    waitForImages(rootEl) {
      const imgs = Array.from(rootEl.querySelectorAll('img'))
      const wait = (img) => {
        if (img.complete && img.naturalHeight !== 0) return Promise.resolve()
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true })
          img.addEventListener('error', resolve, { once: true })
        })
      }
      return Promise.all(imgs.map(wait))
    },
    cleanup() {
      document.body.classList.remove('printing')
      if (this.afterPrintHandler) {
        window.removeEventListener('afterprint', this.afterPrintHandler)
        this.afterPrintHandler = null
      }
      if (this.printArea && this.printArea.parentNode) {
        this.printArea.parentNode.removeChild(this.printArea)
      }
      this.printArea = null
    }
  }
}
</script>
